// ─────────────────────────────────────────────────────────────────────────────
// sawyerApi.ts — Sawyer's page talks to four routes, all under @require_auth:
//
//   GET  /api/media/sawyer/board      every window, composed from his T2 on read
//   POST /api/media/sawyer/gate       Matt's Gate 1 press (pass / kick_back + note)
//   POST /api/media/sawyer/gate-chat  open (pauses the loop) · send · close (resumes)
//   POST /api/media/sawyer/vision     Matt's add/remove instruction from Home
//
// LOOP ON and the lane use the existing routes (/api/rexy/agent-loop and
// /api/rexy/agent-lane) through api/agentLoop.ts and api/agentLane.ts.
// Every POST answers with the board the backend now holds — never an echo.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from '../../api/apiBase';

export interface DocBlock { kind: 'head' | 'action' | 'line_a' | 'line_b'; label?: string; timecode?: string; text?: string; speaker?: string }
export interface SelfCheck { check?: string; pass?: boolean; why?: string }
export interface QueueRow {
  episode_id: string; version?: number; title?: string; runtime_s?: number | string;
  lines?: { a?: number; b?: number } | null; cliffhanger?: string; dropped_at?: string;
  decided_at?: string; state?: 'at_gate' | 'passed' | 'kicked_back' | string;
}
export interface ScriptView {
  episode_id: string; version?: number; title?: string; runtime_s?: number | string; state?: string;
  document: DocBlock[]; self_checks: SelfCheck[]; cliffhanger?: string;
  lines?: { a?: number; b?: number } | null; written_at?: string;
}
export interface ChatMessage { who: string; text: string; at?: string }
export interface ChatView { episode_id: string; version?: number; state?: string; messages: ChatMessage[]; opened_at?: string; closed_at?: string }
export interface KickBack {
  episode_id: string; version_kicked?: number; state?: string; kind?: string; direction?: string;
  keep?: string[]; change?: string[]; rule?: string; kicked_at?: string; fixed_at?: string;
  redo_version?: number; chat?: ChatView | null;
}
export interface Board {
  success: boolean;
  composed_at?: string;
  status: {
    armed?: boolean; paused?: boolean; walking?: boolean; supervising?: boolean;
    step_now?: { file?: string; episode_id?: string | null } | null;
    drawing_on?: { address: string; kind: string; present: boolean }[];
    last_lap?: { seconds?: number; episode_id?: string | null; ended_at?: string } | null;
    stopped?: string | null; last_error?: string | null; vision_error?: string | null;
    cooling_until?: string | null;
  };
  season_id?: string | null;
  vision: { season_id?: string | null; text?: string | null; version?: number | null; updated_at?: string | null;
            instructions: { instruction_id?: string; instruction?: string; sent_at?: string; state?: string; why?: string; done_at?: string }[] };
  season_plan: { season_id?: string | null; present: boolean; story_circle: any[]; settings: any[]; episodes: any[];
                 finale_episode_id?: string | null; progress: { step?: number; name?: string; episodes: string[]; tally: Record<string, number> }[] };
  outlines: { episode_id: string; state?: string | null; title?: string; summary?: string; encounter?: string;
              reversal?: string; cliffhanger_type?: string; pays_off?: string; leaves_thread?: string }[];
  scripts: ScriptView[];
  traces: Record<string, { beat?: string; rule?: string }[]>;
  gate: { number: number; queue: QueueRow[]; waiting: QueueRow[]; records: any[]; chats: ChatView[]; after_gate_notes: any[] };
  today: QueueRow[];
  journeys: { season_summaries: any[]; characters: any[]; relationships: any[] };
  kickbacks: KickBack[];
  gaps: { kind: string; count: number; at: number }[];
  gap_records: { question?: string; subject?: string; recorded_at?: string }[];
  gap_at: number;
  expertise: { name: string; file: string; state: 'loaded' | 'placeholder' | 'not uploaded' }[];
  t1_present: Record<string, boolean>;
  series_bible_present: boolean;
}

const ROOT = '/media/sawyer';

function headers(json = false): Record<string, string> {
  const token = authToken();
  const h: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/** The board, or null when it could not be read (the page says so). */
export async function getBoard(): Promise<Board | null> {
  try {
    const res = await fetch(apiBase() + ROOT + '/board', { headers: headers() });
    if (!res.ok) return null;
    const b = await res.json();
    return b && b.success === true ? (b as Board) : null;
  } catch {
    return null;
  }
}

async function post(path: string, body: unknown): Promise<{ board: Board | null; error: string | null }> {
  try {
    const res = await fetch(apiBase() + ROOT + path, { method: 'POST', headers: headers(true), body: JSON.stringify(body) });
    const b = await res.json().catch(() => null);
    const board = b && b.board && b.board.success === true ? (b.board as Board) : null;
    if (!res.ok || !b || b.success !== true) {
      return { board, error: String((b && b.error) || `refused (HTTP ${res.status})`) };
    }
    return { board, error: null };
  } catch (e: any) {
    return { board: null, error: `not sent (${e?.message || 'no answer'})` };
  }
}

export function gateDecide(episodeId: string, version: number, decision: 'pass' | 'kick_back', note: string) {
  return post('/gate', { episode_id: episodeId, version, decision, note });
}

export function gateChat(action: 'open' | 'send' | 'close', episodeId: string, version: number, text = '') {
  return post('/gate-chat', { action, episode_id: episodeId, version, text });
}

export function sendVision(text: string) {
  return post('/vision', { text });
}
